// Inter-company cost allocation (multi-entity §12.6) — which share of this
// person's salary cost another company in the group bears.
//
// The distinction this screen has to make unmissable, because getting it
// backwards moves real money: these are the shares recharged AWAY from the
// employer. No rows means the employer bears 100%, which is the normal case.
// The employer's own share is therefore never edited here — it is whatever is
// left, and it is shown so nobody has to work that out.
//
// Pay is untouched by any of this. The run, the payslip and the statutory
// filing stay with the employing company; only where the cost is booked moves.
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Building2, Info, Loader2, Plus, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Input } from '@/features/shared/components/ui/input';
import { Badge } from '@/features/shared/components/ui/badge';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/features/shared/components/ui/select';
import { useConfirm } from '@/features/shared/components/confirm/ConfirmProvider';
import { getApiErrorMessage } from '@/lib/apiError';
import { payrollApi } from '@/features/payroll/api/payrollApi';
import type { LegalEntityLite } from '@/features/payroll/types';
import {
  listCostAllocations, createCostAllocation, revokeCostAllocation,
  type CostAllocation, type CostAllocationList,
} from '@/services/payrollSettingsService';

const labelCls = 'text-xs text-muted-foreground';

const BASIS_LABEL: Record<string, string> = {
  employment_cost: 'salary plus employer contributions',
  salary_only: 'salary only',
};

interface Props {
  employeeId: string;
  canEdit: boolean;
}

export const CostAllocationCard: React.FC<Props> = ({ employeeId, canEdit }) => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [data, setData] = useState<CostAllocationList | null>(null);
  const [entities, setEntities] = useState<LegalEntityLite[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [entityId, setEntityId] = useState('');
  const [percent, setPercent] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rows, list] = await Promise.all([
        listCostAllocations(employeeId),
        payrollApi.listLegalEntities(),
      ]);
      setData(rows);
      setEntities(list);
    } catch (err) {
      toast.error(getApiErrorMessage(err, t('costAlloc.loadFailed', {
        defaultValue: 'Could not load cost allocations.',
      })));
    } finally {
      setLoading(false);
    }
  }, [employeeId, t]);

  useEffect(() => { void load(); }, [load]);

  const add = async () => {
    if (!entityId || !percent.trim() || !from) return;
    setSaving(true);
    try {
      await createCostAllocation(employeeId, {
        legal_entity_id: entityId,
        percent: percent.trim(),
        effective_from: from,
        effective_to: to || undefined,
      });
      setEntityId(''); setPercent(''); setFrom(''); setTo('');
      await load();
      toast.success(t('costAlloc.added', { defaultValue: 'Cost allocation added.' }));
    } catch (err) {
      // The backend refuses over-allocation and one-sided authority with a
      // message that explains which; surfacing it verbatim is the whole point.
      toast.error(getApiErrorMessage(err, t('costAlloc.addFailed', {
        defaultValue: 'Could not add the allocation.',
      })));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (a: CostAllocation) => {
    const where = a.entity_name || a.legal_entity_id;
    const ok = await confirm({
      title: t('costAlloc.removeTitle', { defaultValue: 'Stop this cost allocation?' }),
      message: t('costAlloc.removeDesc', {
        defaultValue:
          '{{where}} will stop bearing {{percent}}% of this person’s cost from the next payroll run. Runs already calculated keep the share they were calculated with.',
        where, percent: a.percent,
      }),
      confirmLabel: t('costAlloc.remove', { defaultValue: 'Stop allocation' }),
      danger: true,
    });
    if (!ok) return;
    try {
      await revokeCostAllocation(a.id);
      await load();
      toast.success(t('costAlloc.removed', { defaultValue: 'Allocation stopped.' }));
    } catch (err) {
      toast.error(getApiErrorMessage(err, t('costAlloc.removeFailed', {
        defaultValue: 'Could not stop the allocation.',
      })));
    }
  };

  // With one company there is nothing to allocate: cost has nowhere else to go.
  if (loading || entities.length < 2) return null;

  const rows = data?.allocations ?? [];
  const employerPercent = data?.employer_percent ?? '100.00';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-sm font-medium">
          <Building2 className="h-4 w-4 text-muted-foreground" aria-hidden />
          {t('costAlloc.title', { defaultValue: 'Who bears this cost' })}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-start gap-2 rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
          <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span>
            {t('costAlloc.help', {
              defaultValue:
                'Recharge part of this person’s cost to another company in the group, at cost. Pay does not move — the payslip and the statutory filing stay with their employer. Only where the cost is booked changes.',
            })}
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-2 text-xs">
          <Badge variant="outline">
            {t('costAlloc.employerShare', {
              defaultValue: 'Employer bears {{percent}}%', percent: employerPercent,
            })}
          </Badge>
          {data?.basis && (
            <span className="text-muted-foreground">
              {t('costAlloc.basis', {
                defaultValue: 'Applied to {{basis}}',
                basis: BASIS_LABEL[data.basis] ?? data.basis,
              })}
            </span>
          )}
        </div>

        {rows.length > 0 && (
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full text-left text-xs">
              <thead className="bg-muted/50 text-muted-foreground">
                <tr>
                  <th scope="col" className="px-3 py-2 font-medium">{t('costAlloc.company', { defaultValue: 'Company' })}</th>
                  <th scope="col" className="px-3 py-2 font-medium">{t('costAlloc.share', { defaultValue: 'Share' })}</th>
                  <th scope="col" className="px-3 py-2 font-medium">{t('costAlloc.period', { defaultValue: 'From / to' })}</th>
                  {canEdit && <th scope="col" className="px-3 py-2" />}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((a) => (
                  <tr key={a.id}>
                    <td className="px-3 py-2 text-foreground">{a.entity_name || a.legal_entity_id}</td>
                    <td className="px-3 py-2 text-foreground">{a.percent}%</td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {a.effective_from}
                      {a.effective_to
                        ? ` → ${a.effective_to}`
                        : ` → ${t('costAlloc.ongoing', { defaultValue: 'ongoing' })}`}
                    </td>
                    {canEdit && (
                      <td className="px-3 py-2 text-right">
                        <Button size="sm" variant="ghost" onClick={() => void remove(a)}
                          aria-label={t('costAlloc.removeFor', {
                            defaultValue: 'Stop the allocation to {{where}}',
                            where: a.entity_name || a.legal_entity_id,
                          })}>
                          <X className="h-4 w-4" aria-hidden />
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {rows.length === 0 && (
          <p className="text-xs text-muted-foreground">
            {t('costAlloc.none', {
              defaultValue: 'Their employer bears the whole cost. That is the usual arrangement.',
            })}
          </p>
        )}

        {canEdit && (
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <label className={labelCls} htmlFor="ca-company">
                {t('costAlloc.company', { defaultValue: 'Company' })}
              </label>
              <Select value={entityId} onValueChange={setEntityId}>
                <SelectTrigger id="ca-company" className="h-9 w-[14rem]">
                  <SelectValue placeholder={t('costAlloc.pickCompany', { defaultValue: 'Pick a company' })} />
                </SelectTrigger>
                <SelectContent>
                  {entities.map((e) => (
                    <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className={labelCls} htmlFor="ca-percent">
                {t('costAlloc.share', { defaultValue: 'Share' })} (%)
              </label>
              <Input id="ca-percent" className="h-9 w-[6rem]" inputMode="decimal"
                value={percent} onChange={(e) => setPercent(e.target.value)} placeholder="40" />
            </div>
            <div>
              <label className={labelCls} htmlFor="ca-from">
                {t('costAlloc.from', { defaultValue: 'From' })}
              </label>
              <Input id="ca-from" type="date" className="h-9 w-[10rem]"
                value={from} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div>
              <label className={labelCls} htmlFor="ca-to">
                {t('costAlloc.to', { defaultValue: 'To (optional)' })}
              </label>
              <Input id="ca-to" type="date" className="h-9 w-[10rem]"
                value={to} onChange={(e) => setTo(e.target.value)} />
            </div>
            <Button size="sm" disabled={saving || !entityId || !percent.trim() || !from}
              onClick={() => void add()}>
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                      : <Plus className="mr-2 h-4 w-4" aria-hidden />}
              {t('costAlloc.add', { defaultValue: 'Add allocation' })}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CostAllocationCard;
